import type { BillingAllowance } from './useBillingAllowance';
import { ALLOWANCE_WARNING_SHARE, BILLING_PLAN_CATALOGUE } from 'definitions/billing-plans';
import { formatBillingDate, formatUnits } from './billing-format';

export type UsageMeter = {
  /** "12,400 / 100,000", or "12,400" on Unlimited. Null when the account has no plan to measure. */
  sessions: { text: string; ratio: number | null; warn: boolean; over: boolean } | null;
  /** The account has no active plan (billing-active.ts): it is not serving and is read-only, so there is no usage to show. */
  noPlan: boolean;
  /** What the hover says, one line each: the figures in words, when they reset, and what the colour means. */
  details: string[];
};

type UsageMeterParams = {
  allowance: BillingAllowance | null;
  /** The server's answer to "does this account have a plan that serves"; undefined while it loads. */
  billingActive: boolean | undefined;
};

/**
 * What the topbar shows for the account in scope: sessions counted in the current window
 * against the plan. The AI credits have a meter of their own beside it (ai-credit-meter.ts).
 * Nothing to show returns null, so an agency without a client selected (no plan) gets no meter. An account
 * whose plan is not active says so instead, whatever stale figures remain: a counter against a plan
 * it no longer has would mislead.
 */
export const usageMeter = ({ allowance, billingActive }: UsageMeterParams): UsageMeter | null => {
  if (billingActive === false)
    return {
      sessions: null,
      noPlan: true,
      details: ['No active plan', "Nothing serves to this account's websites until a plan is chosen."],
    };
  const sessions = allowance
    ? {
        text:
          allowance.included === null
            ? formatUnits(allowance.used)
            : `${formatUnits(allowance.used)} / ${formatUnits(allowance.included)}`,
        ratio: allowance.included ? Math.min(allowance.used / allowance.included, 1) : null,
        // From 80% of the plan's sessions, the same mark the first notice is sent at.
        warn: allowance.included !== null && allowance.used >= allowance.included * ALLOWANCE_WARNING_SHARE,
        over: allowance.included !== null && allowance.used > allowance.included,
      }
    : null;
  if (!sessions) return null;
  return { sessions, noPlan: false, details: sessionDetails({ allowance, sessions }) };
};

type SessionDetailsParams = { allowance: BillingAllowance | null; sessions: UsageMeter['sessions'] };

/** The session figures in words, and what a yellow or red meter means for this account. */
function sessionDetails({ allowance, sessions }: SessionDetailsParams): string[] {
  if (!allowance || !sessions) return [];
  const { plan, included, used, windowEnd, held, capped } = allowance;
  const name = BILLING_PLAN_CATALOGUE[plan].name;
  const lines =
    included === null
      ? [`Sessions this period: ${formatUnits(used)} (${name} has no session limit)`]
      : [
          `Sessions this period: ${formatUnits(used)} of ${formatUnits(included)} (${Math.round((used / included) * 100)}%)`,
        ];
  lines.push(`Resets ${formatBillingDate(windowEnd)}`);

  if (capped) {
    lines.push(`You chose to stay on ${name}, so further sessions are ignored until it resets.`);
  } else if (held && included !== null) {
    lines.push(`You are staying on ${name}: sessions over the limit are ignored until it resets.`);
  } else if (sessions.over) {
    lines.push('Over your plan. A single month over is free; two months in a row moves you up a plan.');
  } else if (sessions.warn) {
    lines.push('Nothing stops if you go over: one month over is free.');
  }
  return lines;
}
